import { Group, Mesh } from 'three';
import gsap from 'gsap';
import { Component3D } from './Component3D';
import { SIZE_OPTIONS, SIZE_REFS_MAP, UNIT } from '../../../constants/sizes';
import { getTextLayoutStyle, getTextMetrics, getTitleMetrics } from './metrics/QuestionMetrics';
import {
    COLOR_DEFAULT_TEXT, COLOR_OPTION_BUTTON_CORRECT, COLOR_OPTION_BUTTON_DEFAULT,
    COLOR_OPTION_BUTTON_DEFAULT_TEXT,
    COLOR_OPTION_BUTTON_HOVER, COLOR_OPTION_BUTTON_NOT_BAD, COLOR_OPTION_BUTTON_WORST, COLOR_OPTION_BUTTON_WORST_TEXT
} from '../../../constants/colors';
import Text3D from './Text3D';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { materialsLib } from '../../../../data/settings';
import { QuizActionEvent, QuizActionType } from '../../../constants/QuizActionEvent';
import THREE from '../utils/threeClassesAdapter';
import { AnswerOptionStatus } from '../../../constants/AnswerOptionStatus';
import { RoundedRect } from '../geometries/RoundedRect';
import { HalfRoundedRect } from '../geometries/HalfRoundedRect';

// import Text3D from "./Text3D";

export class Question3D extends Component3D {
    #id;
    #data;
    #pagination;
    #titleG;
    #optionsG;
    #backG;
    #rootG;
    #title3d;
    #pagination3d;
    #titleBack3d;
    #back3d;
    #optionsMap;
    #optionsSetFromEnd;
    #optionsHeight = 0;
    #active = true;
    #isStarted = false;

    constructor(data, index, numTotal) {
        super();

        const { id } = data || {};

        this.#id = id || '';
        this.#data = data;
        this.#pagination = `${index + 1} из ${numTotal}`;

        const titleG = new Group();
        const optionsG = new Group();
        const backG = new Group();

        titleG.position.set(0, UNIT.questionY, 0);
        optionsG.position.set(0, UNIT.questionY, 0);
        backG.position.set(0, UNIT.questionY, 0);

        this.add(titleG);
        this.add(optionsG);
        this.add(backG);

        // titleG.visible = false;
        // optionsG.visible = false;
        // backG.visible = false;

        this.#titleG = titleG;
        this.#optionsG = optionsG;
        this.#backG = backG;
    }

    _setup() {
        super._setup();
        this.waitForPreload = true;
        this.hasResize = true;
        this.hasStart = true;
        this.clickable = true;
    }

    _resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio) {
        super._resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio);

        if (orientationChanged) {
            const { questionYCorrection, questionScale } = SIZE_REFS_MAP[orientation];

            this.#titleG.position.set(0, UNIT.questionY + questionYCorrection, 0);
            this.#optionsG.position.set(0, UNIT.questionY + questionYCorrection, 0);
            this.#backG.position.set(0, UNIT.questionY + questionYCorrection, 0);

            this.#titleG.scale.set(questionScale, questionScale, questionScale);
            this.#optionsG.scale.set(questionScale, questionScale, questionScale);
            this.#backG.scale.set(questionScale, questionScale, questionScale);

            this.stop();
            this._updateTextMetricsAndPosition(orientation);
            this.start();
        }
    }

    start() {
        if (this.#isStarted) return;
        if (!this.clickable) return;

        if (!this.clickActions || !this._mainClickTarget) return;

        const targets = this._mainClickTarget;

        this._onInteraction();

        const clickHandlers = [];
        const overHandlers = [];
        const outHandlers = [];

        targets.forEach((target, index) => {
            target.layers.set(1);
            target.cursor = 'pointer';

            const handler = () => {
                const action = this.clickActions[index];

                if (!action) return;
                this.dispatchAction(action);
            };

            const over = () => {
                const action = this.clickActions[index];

                if (!action) return;
                this.setOver(action.value);
            };

            const out = () => {
                const action = this.clickActions[index];

                if (!action) return;
                this.setOut(action.value);
            };

            overHandlers.push(over);
            outHandlers.push(out);
            clickHandlers.push(handler);

            target.on('click', handler);
            target.on('mouseover', over);
            target.on('mouseout', out);

            return handler;
        });

        this._handlers = clickHandlers;
        this._overHandlers = overHandlers;
        this._outHandlers = outHandlers;
        this.#isStarted = true;
    }

    stop() {
        const targets = this._mainClickTarget;

        this._offInteraction();

        if (this._handlers && targets) {
            targets.forEach((target, index) => {
                target.layers.set(0);
                target.cursor = 'default';

                const handler = this._handlers[index];
                const over = this._overHandlers[index];
                const out = this._outHandlers[index];

                target.off('click', handler);
                target.off('mouseover', over);
                target.off('mouseout', out);

                return handler;
            });
        }

        this._handlers = undefined;
        this._overHandlers = undefined;
        this._outHandlers = undefined;
        this.#isStarted = false;
    }

    destroy() {
        this.stop();
        super.destroy();

        gsap.killTweensOf(this);

        this.#optionsMap.forEach(({ text3d, back3d }) => {
            gsap.killTweensOf(back3d.material.color);
            gsap.killTweensOf(text3d.color);
        });
    }

    setupPreloadedContent(infrastructure) {
        const { text, options } = this.#data || {};

        const metricsTtl = getTitleMetrics(SIZE_OPTIONS.LANDSCAPE);
        const metricsTxt = getTextMetrics(SIZE_OPTIONS.LANDSCAPE);
        const metricsLayout = getTextLayoutStyle(SIZE_OPTIONS.LANDSCAPE);
        const {
            deltaWidth, deltaHeight, roundRadius, optionRelativeWidth, optionMargin, optionDeltaWidth, optionDeltaHeight
        } = metricsLayout;

        // TITLE
        const title3d = new Text3D({
            ...metricsTtl,
            color: COLOR_DEFAULT_TEXT,
            valign: 'bottom',
            type: 'textMaterial2Bent',
            bendingRadius: UNIT.questionR
        }, text, infrastructure);
        const pagination3d = new Text3D({
            ...metricsTxt,
            width: metricsTtl.width * 1.04,
            color: COLOR_DEFAULT_TEXT,
            valign: 'bottom',
            type: 'textMaterial2Bent',
            bendingRadius: UNIT.questionR
        }, this.#pagination, infrastructure);
        const { lineHeight: ttlLineHeight } = title3d;

        const ttlBlockHeight = title3d.textHeight + ttlLineHeight * deltaHeight;
        let geom = new HalfRoundedRect(
            title3d.textWidth + ttlLineHeight * deltaWidth,
            ttlBlockHeight,
            ttlLineHeight * roundRadius,
            UNIT.questionR,
            10,
            40
        );

        const titleBack3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.questionTitleBackBasic)
        );

        // OPTIONS
        let bottom = 0;
        const optionsTexts = [...options].reverse().map((option, index) => {
            const { id, label } = option;
            const text3d = new Text3D({
                ...metricsTxt,
                width: optionRelativeWidth * metricsTtl.width,
                color: COLOR_OPTION_BUTTON_DEFAULT_TEXT,
                valign: 'center',
                type: 'textMaterial2Bent',
                bendingRadius: UNIT.questionR
            }, label, infrastructure);
            const { textWidth, lineHeight: opLineHeight, textHeight } = text3d;

            const opTextH = textHeight + opLineHeight * optionDeltaHeight;
            const geom1 = new RoundedRect(
                textWidth + opLineHeight * optionDeltaWidth,
                opTextH,
                Math.min(opTextH * 0.5, ttlLineHeight * roundRadius),
                UNIT.questionR,
                10,
                40
            );

            const opnBack3d = new Mesh(
                geom1,
                MaterialsFactory.create(materialsLib.questionOptionBackBasic)
            );

            if (index === 0) bottom += optionMargin * opLineHeight * 1.4;
            let y = 0.5 * opTextH;

            // y += 0.5 * optionMargin * opLineHeight;

            y += bottom;

            text3d.position.set(0, y, 0.1);
            opnBack3d.position.set(0, y, 0.02);

            this.#optionsG.add(opnBack3d);
            this.#optionsG.add(text3d);

            bottom = y + 0.5 * opTextH;
            bottom += optionMargin * opLineHeight;

            return [id, { text3d, back3d: opnBack3d }];
        }).reverse();

        // TITLE POSITION
        title3d.position.y = 0.5 * ttlLineHeight * deltaHeight + bottom;
        title3d.position.z = 0.1;
        pagination3d.position.y = ttlBlockHeight + bottom + pagination3d.lineHeight * 0.5;
        geom.translate(0, 0.5 * ttlBlockHeight + bottom, 0);

        // OPTIONS BACK
        geom = new HalfRoundedRect(
            title3d.textWidth + ttlLineHeight * deltaWidth,
            bottom,
            ttlLineHeight * roundRadius,
            UNIT.questionR,
            10,
            60
        );

        geom.rotateZ(Math.PI);
        geom.translate(0, -bottom * 0.5 + bottom, -0.1);

        const back3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.questionBackBasic)
        );

        this.#optionsHeight = Math.abs(bottom);

        this.#titleG.add(titleBack3d);
        this.#titleG.add(title3d);
        this.#titleG.add(pagination3d);
        this.#backG.add(back3d);

        this.#title3d = title3d;
        this.#pagination3d = pagination3d;
        this.#titleBack3d = titleBack3d;
        this.#back3d = back3d;
        this.#optionsSetFromEnd = [...optionsTexts].reverse();
        this.#optionsMap = new Map(optionsTexts);
        this._mainClickTarget = optionsTexts.map(([, { back3d: mesh }]) => mesh);
        this.clickActions = optionsTexts.map(([id]) => ({
            type: QuizActionType.ANSWER,
            value: id,
            questionId: this.#id
        }));
    }

    _onInteraction() {
        this.layers.set(1);
        this.#optionsG.layers.set(1);
    }

    _offInteraction() {
        this.layers.set(0);
        this.#optionsG.layers.set(0);
    }

    dispatchAction(action) {
        if (!this.clickable || !this.#active) return;

        this.dispatchEvent({
            type: QuizActionEvent,
            action
        });
    }

    disable() {
        this.#active = false;
        this._offInteraction();
    }

    _updateTextMetricsAndPosition(orientation) {
        const metricsTtl = getTitleMetrics(orientation);
        const metricsTxt = getTextMetrics(orientation);
        const metricsLayout = getTextLayoutStyle(orientation);
        const {
            deltaWidth, deltaHeight, roundRadius, optionRelativeWidth, optionMargin, optionDeltaWidth, optionDeltaHeight
        } = metricsLayout;

        const title3d = this.#title3d;
        const pagination3d = this.#pagination3d;
        let optionsSetFromEnd = this.#optionsSetFromEnd;
        let titleBack3d = this.#titleBack3d;
        let back3d = this.#back3d;

        if (titleBack3d) this.#titleG.remove(titleBack3d);
        if (back3d) this.#backG.remove(back3d);

        // TITLE
        title3d.setMetrics({
            ...metricsTtl,
            valign: 'bottom'
        });

        pagination3d.setMetrics({
            ...metricsTxt,
            width: metricsTtl.width * 1.04,
            valign: 'bottom'
        });

        const { lineHeight: ttlLineHeight } = title3d;

        const ttlBlockHeight = title3d.textHeight + ttlLineHeight * deltaHeight;
        let geom = new HalfRoundedRect(
            title3d.textWidth + ttlLineHeight * deltaWidth,
            ttlBlockHeight,
            ttlLineHeight * roundRadius,
            UNIT.questionR,
            10,
            40
        );

        titleBack3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.questionTitleBackBasic)
        );

        // OPTIONS
        let bottom = 0;

        optionsSetFromEnd = optionsSetFromEnd.map(([id, { text3d, back3d: opnBack3d }], index) => {
            if (opnBack3d) this.#optionsG.remove(opnBack3d);

            text3d.setMetrics({
                ...metricsTxt,
                width: optionRelativeWidth * metricsTtl.width,
                valign: 'center'
            });

            const { textWidth, lineHeight: opLineHeight, textHeight } = text3d;

            // console.log(textWidth, deltaWidth, roundRadius, optionDeltaWidth);

            const opTextH = textHeight + opLineHeight * optionDeltaHeight;
            const geom1 = new RoundedRect(
                textWidth + opLineHeight * optionDeltaWidth,
                opTextH,
                Math.min(opTextH * 0.5, ttlLineHeight * roundRadius),
                UNIT.questionR,
                10,
                40
            );

            opnBack3d = new Mesh(
                geom1,
                MaterialsFactory.create(materialsLib.questionOptionBackBasic)
            );

            if (index === 0) bottom += optionMargin * opLineHeight * 1.4;
            let y = 0.5 * opTextH;

            y += bottom;

            text3d.position.y = y;
            opnBack3d.position.y = y;

            this.#optionsG.add(opnBack3d);

            bottom = y + 0.5 * opTextH;
            bottom += optionMargin * opLineHeight;

            return [id, { text3d, back3d: opnBack3d }];
        });

        // TITLE POSITION
        title3d.position.y = 0.5 * ttlLineHeight * deltaHeight + bottom;
        pagination3d.position.y = ttlBlockHeight + bottom + pagination3d.lineHeight * 0.5;
        geom.translate(0, 0.5 * ttlBlockHeight + bottom, 0);

        // OPTIONS BACK
        geom = new HalfRoundedRect(
            title3d.textWidth + ttlLineHeight * deltaWidth,
            bottom * 1.03,
            ttlLineHeight * roundRadius,
            UNIT.questionR,
            10,
            60
        );

        geom.rotateZ(Math.PI);
        geom.translate(0, -bottom * 0.5 + bottom, -0.1);

        back3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.questionBackBasic)
        );

        this.#titleG.add(titleBack3d);
        this.#backG.add(back3d);

        this.#titleBack3d = titleBack3d;
        this.#back3d = back3d;
        this.#optionsSetFromEnd = optionsSetFromEnd;
        this.#optionsMap = new Map(optionsSetFromEnd);
        this._mainClickTarget = [...optionsSetFromEnd].reverse().map(([, { back3d: mesh }]) => mesh);

        this.#optionsHeight = Math.abs(bottom);
    }

    // OPTIONS STATES
    setOver(optionValue) {
        if (!this.#active) return;

        const option = this.#optionsMap.get(optionValue);

        if (!option) return;

        const newColor = new THREE.Color(COLOR_OPTION_BUTTON_HOVER);

        gsap.to(option.back3d.material.color, {
            r: newColor.r,
            g: newColor.g,
            b: newColor.b,
            duration: 0.25,
            ease: 'pow2.inOut',
            overwrite: 'auto'
        });
    }

    setOut(optionValue) {
        if (!this.#active) return;

        const option = this.#optionsMap.get(optionValue);

        if (!option) return;

        const newColor = new THREE.Color(COLOR_OPTION_BUTTON_DEFAULT);

        gsap.to(option.back3d.material.color, {
            r: newColor.r,
            g: newColor.g,
            b: newColor.b,
            duration: 0.45,
            ease: 'pow2.inOut',
            overwrite: 'auto'
        });
    }

    displayAnswerStatus(status) {
        status.forEach(({ id, status: opStatus }) => {
            const option = this.#optionsMap.get(id);

            if (!option) return;

            let newColor = null;

            // eslint-disable-next-line default-case
            switch (opStatus) {
                case AnswerOptionStatus.CORRECT: {
                    newColor = new THREE.Color(COLOR_OPTION_BUTTON_CORRECT);

                    break;
                }
                case AnswerOptionStatus.OK: {
                    newColor = new THREE.Color(COLOR_OPTION_BUTTON_NOT_BAD);

                    break;
                }
                case AnswerOptionStatus.INCORRECT: {
                    newColor = new THREE.Color(COLOR_OPTION_BUTTON_WORST);

                    break;
                }
            }

            if (!newColor) return;

            gsap.to(option.back3d.material.color, {
                r: newColor.r,
                g: newColor.g,
                b: newColor.b,
                duration: 0.45,
                ease: 'pow2.inOut',
                overwrite: 'auto'
            });

            if (opStatus === AnswerOptionStatus.INCORRECT) {
                const textColor = new THREE.Color(COLOR_OPTION_BUTTON_WORST_TEXT);

                gsap.to(option.text3d.color, {
                    r: textColor.r,
                    g: textColor.g,
                    b: textColor.b,
                    duration: 0.45,
                    ease: 'pow2.inOut',
                    overwrite: 'auto'
                });
            }
        });
    }
}
